import { useCallback, useEffect, useMemo, useState } from 'react'
import { App as AntdApp, Button, Card, Empty, Flex, Segmented, Spin, Typography } from 'antd'
import { CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, MailOutlined, ReloadOutlined } from '@ant-design/icons'
import type { EmailSummary } from '../../shared/services/api'
import { getApiService } from '../../shared/services/api'
import { MetricCard } from '../../shared/ui/MetricCard'
import { PageHeader } from '../../shared/ui/PageHeader'

export function DashboardPage() {
  const { message } = AntdApp.useApp()
  const api = useMemo(() => getApiService(), [])
  const [days, setDays] = useState(14)
  const [data, setData] = useState<EmailSummary | null>(null)
  const [loading, setLoading] = useState(false)
  const load = useCallback(async () => {
    setLoading(true)
    try { setData((await api.emailSummary(days)).data) }
    catch { message.error('Не удалось загрузить сводку отправок') }
    finally { setLoading(false) }
  }, [api, days])
  useEffect(() => { void load() }, [load])

  const totals = data?.totals ?? {}
  const failed = ['insufficient_balance', 'rejected', 'permanent_failed', 'submission_failed', 'delivery_unknown'].reduce((sum, key) => sum + Number(totals[key] ?? 0), 0)
  const delivered = Number(totals.delivered ?? 0)
  const total = Number(totals.total ?? 0)
  const processing = Math.max(0, total - delivered - failed)

  return (
    <Flex vertical gap="middle">
      <PageHeader
        title="Обзор отправок"
        description="Окончательные состояния писем и текущая очередь Mailgun."
        icon={<MailOutlined />}
        extra={<Flex gap={8}><Segmented options={[{ label: '7 дней', value: 7 }, { label: '14 дней', value: 14 }, { label: '30 дней', value: 30 }]} value={days} onChange={(value) => setDays(Number(value))} /><Button icon={<ReloadOutlined />} loading={loading} onClick={() => void load()}>Обновить</Button></Flex>}
      />
      <div className="metric-grid">
        <MetricCard title="Всего писем" value={total} detail={`За последние ${days} дней`} icon={<MailOutlined />} tone="blue" />
        <MetricCard title="Доставлено" value={delivered} detail={total ? `${Math.round(delivered / total * 100)}% от всех писем` : 'Нет отправок'} icon={<CheckCircleOutlined />} tone="green" />
        <MetricCard title="В процессе" value={processing} detail="Ожидают окончательного ответа" icon={<ClockCircleOutlined />} tone="amber" />
        <MetricCard title="Не доставлено" value={failed} detail="Окончательные отказы и ошибки" icon={<CloseCircleOutlined />} tone="red" />
      </div>
      <Card className="content-card mail-volume-card" title="Отправки по дням">
        {loading && !data ? <div className="mail-summary-loading"><Spin /></div> : data?.daily.length ? <DailyChart rows={data.daily} /> : <Empty description="За выбранный период отправок не было" />}
      </Card>
    </Flex>
  )
}

function DailyChart({ rows }: { rows: EmailSummary['daily'] }) {
  const max = Math.max(1, ...rows.map((row) => Number(row.total)))
  return (
    <div className="mail-volume-chart" role="img" aria-label="Количество отправок по дням">
      {rows.map((row) => {
        const total = Number(row.total)
        const delivered = Number(row.delivered)
        const failed = Number(row.failed)
        const date = new Date(row.day)
        return (
          <div className="mail-volume-column" key={String(row.day)} title={`${date.toLocaleDateString('ru-RU')}: ${total}`}>
            <Typography.Text className="mail-volume-value">{total}</Typography.Text>
            <div className="mail-volume-track" style={{ height: `${Math.max(8, total / max * 180)}px` }}>
              <div className="mail-volume-delivered" style={{ height: `${total ? delivered / total * 100 : 0}%` }} />
              <div className="mail-volume-failed" style={{ height: `${total ? failed / total * 100 : 0}%` }} />
            </div>
            <Typography.Text type="secondary" className="mail-volume-label">{date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })}</Typography.Text>
          </div>
        )
      })}
    </div>
  )
}
